import React, { useEffect, useState } from 'react';
import { marked } from 'marked';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  OpenInNew as OpenInNewIcon,
  Public as PublicIcon,
  Slideshow as SlideshowIcon,
} from '@mui/icons-material';
import DuplicatePracticeBank from './DuplicatePracticeBank';
import MissingnessPracticeBank from './MissingnessPracticeBank';
import OutlierPracticeBank from './OutlierPracticeBank';
import TakeHomePracticeBank from './TakeHomePracticeBank';
import { DQ_MATH_05_CONFIG } from '../../data/practiceBankConfigs/dqMath05InvalidValues';
import { STATS_CLINIC_01A_CONFIG } from '../../data/practiceBankConfigs/statisticsClinic01aCentreSpread';
import { STATS_CLINIC_01B_CONFIG } from '../../data/practiceBankConfigs/statisticsClinic01bPositionRelationship';
import { STATS_CLINIC_03_CONFIG } from '../../data/practiceBankConfigs/statisticsClinic03OutliersSamplingBias';
import { STATS_CLINIC_03A_CONFIG } from '../../data/practiceBankConfigs/statisticsClinic03aPosition';
import { STATS_CLINIC_04_CONFIG } from '../../data/practiceBankConfigs/statisticsClinic04NormalDistributionZScores';
import { STATS_CLINIC_05_CONFIG } from '../../data/practiceBankConfigs/statisticsClinic05ProbabilityEmpiricalRule';
import { STATS_CLINIC_06_CONFIG } from '../../data/practiceBankConfigs/statisticsClinic06HypothesisABTesting';
import { STATS_CLINIC_07_CONFIG } from '../../data/practiceBankConfigs/statisticsClinic07PValuesConfidenceIntervals';
import { STATS_CLINIC_08A_CONFIG } from '../../data/practiceBankConfigs/statisticsClinic08aSkewness';
import { STATS_CLINIC_08B_CONFIG } from '../../data/practiceBankConfigs/statisticsClinic08bKurtosis';
import { EXCEL_ANALYST_CHALLENGE_CONFIG } from '../../data/practiceBankConfigs/excelAnalystChallengeScenarios';
import { CT08_TABLE_DETECTIVE_CONFIG } from '../../data/practiceBankConfigs/ct08TableDetective';
import { SQL02_DATA_MODELLING_CONFIG } from '../../data/practiceBankConfigs/sql02DataModelling';
import { SQL03_DATABASE_FUNDAMENTALS_CONFIG } from '../../data/practiceBankConfigs/databaseFundamentals';
import { SQL04_OLTP_OLAP_CONFIG } from '../../data/practiceBankConfigs/oltpOlap';
import { CT10_REQUEST_TO_SPEC_CONFIG } from '../../data/practiceBankConfigs/ct10RequestToSpec';
import { DM05_CONFIG, DM08_CONFIG } from '../../data/practiceBankConfigs/dataModellingTechniques';

export const TRACK_SCOPE_OPTIONS = [
  { value: 'all', label: 'All cohorts' },
  { value: 'data-analyst-job-ready', label: 'Data Analyst' },
  { value: 'sql-job-ready', label: 'SQL' },
  { value: 'python-job-ready', label: 'Python' },
  { value: 'full-stack-developer-job-ready', label: 'Full-Stack' },
  { value: 'azure-data-engineer', label: 'Azure Data Engineer' },
];

const DUPLICATE_PRACTICE_RESOURCE = '/data/guides/dq-math-03-uniqueness-duplicates-sample-tables.md';
const MISSINGNESS_PRACTICE_RESOURCE = '/data/guides/dq-math-02-missing-data-sample-tables.md';
const OUTLIER_PRACTICE_RESOURCE = '/data/guides/dq-math-04-outliers-accuracy-sample-tables.md';
const DQ_MATH_05_PRACTICE_RESOURCE = '/data/guides/dq-math-05-invalid-values-rule-violations-solved-examples.md';
const STATS_CLINIC_01A_PRACTICE_RESOURCE = '/data/guides/statistics_clinic_01a_practice_centre_spread.md';
const STATS_CLINIC_01B_PRACTICE_RESOURCE = '/data/guides/statistics_clinic_01b_practice_position_relationship.md';
const STATS_CLINIC_03_PRACTICE_RESOURCE = '/data/guides/statistics_clinic_03_practice_outliers_sampling_bias.md';
const STATS_CLINIC_03A_PRACTICE_RESOURCE = '/data/guides/statistics_clinic_03a_practice_position.md';
const STATS_CLINIC_04_PRACTICE_RESOURCE = '/data/guides/statistics_clinic_04_practice_normal_distribution_zscores.md';
const STATS_CLINIC_05_PRACTICE_RESOURCE = '/data/guides/statistics_clinic_05_practice_probability_empirical_rule.md';
const STATS_CLINIC_06_PRACTICE_RESOURCE = '/data/guides/statistics_clinic_06_practice_hypothesis_ab_testing.md';
const STATS_CLINIC_07_PRACTICE_RESOURCE = '/data/guides/statistics_clinic_07_practice_pvalues_confidence_intervals.md';
const STATS_CLINIC_08A_PRACTICE_RESOURCE = '/data/guides/statistics_clinic_08a_practice_skewness.md';
const STATS_CLINIC_08B_PRACTICE_RESOURCE = '/data/guides/statistics_clinic_08b_practice_kurtosis.md';
const EXCEL_ANALYST_CHALLENGE_RESOURCE = '/data/guides/excel-analyst-challenge-5-scenarios.md';
const CT08_TABLE_DETECTIVE_RESOURCE = '/data/guides/table-detective-exercise.md';
const SQL02_DATA_MODELLING_RESOURCE = '/data/guides/sql-02-data-modelling-masterclass-course.md';
const SQL03_DATABASE_FUNDAMENTALS_RESOURCE = '/data/guides/sql-03-database-fundamentals-guide.md';
const SQL04_OLTP_OLAP_RESOURCE = '/data/guides/sql-04-oltp-olap-guide.md';
const CT10_REQUEST_TO_SPEC_RESOURCE = '/data/guides/ct-10-request-to-spec-course.md';
const DM05_RESOURCE = '/data/guides/dm-05-kimball-fact-tables.md';
const DM08_RESOURCE = '/data/guides/dm-08-data-vault-foundations.md';

const STATS_CLINIC_CONFIG_BY_RESOURCE = {
  [STATS_CLINIC_01A_PRACTICE_RESOURCE]: STATS_CLINIC_01A_CONFIG,
  [STATS_CLINIC_01B_PRACTICE_RESOURCE]: STATS_CLINIC_01B_CONFIG,
  [STATS_CLINIC_03_PRACTICE_RESOURCE]: STATS_CLINIC_03_CONFIG,
  [STATS_CLINIC_03A_PRACTICE_RESOURCE]: STATS_CLINIC_03A_CONFIG,
  [STATS_CLINIC_04_PRACTICE_RESOURCE]: STATS_CLINIC_04_CONFIG,
  [STATS_CLINIC_05_PRACTICE_RESOURCE]: STATS_CLINIC_05_CONFIG,
  [STATS_CLINIC_06_PRACTICE_RESOURCE]: STATS_CLINIC_06_CONFIG,
  [STATS_CLINIC_07_PRACTICE_RESOURCE]: STATS_CLINIC_07_CONFIG,
  [STATS_CLINIC_08A_PRACTICE_RESOURCE]: STATS_CLINIC_08A_CONFIG,
  [STATS_CLINIC_08B_PRACTICE_RESOURCE]: STATS_CLINIC_08B_CONFIG,
  [EXCEL_ANALYST_CHALLENGE_RESOURCE]: EXCEL_ANALYST_CHALLENGE_CONFIG,
  [CT08_TABLE_DETECTIVE_RESOURCE]: CT08_TABLE_DETECTIVE_CONFIG,
  [SQL02_DATA_MODELLING_RESOURCE]: SQL02_DATA_MODELLING_CONFIG,
  [SQL03_DATABASE_FUNDAMENTALS_RESOURCE]: SQL03_DATABASE_FUNDAMENTALS_CONFIG,
  [SQL04_OLTP_OLAP_RESOURCE]: SQL04_OLTP_OLAP_CONFIG,
  [CT10_REQUEST_TO_SPEC_RESOURCE]: CT10_REQUEST_TO_SPEC_CONFIG,
  [DM05_RESOURCE]: DM05_CONFIG,
  [DM08_RESOURCE]: DM08_CONFIG,
};

const collapseDetailsByDefault = (markdown = '') =>
  markdown.replace(/<details\s+open(?=[\s>])/gi, '<details');

function getTrackMeta(track) {
  switch (track) {
    case 'sql-job-ready':          return { badge: 'SQL', palette: 'success' };
    case 'python-job-ready':       return { badge: 'PY',  palette: 'warning' };
    case 'full-stack-developer-job-ready': return { badge: 'FS', palette: 'primary' };
    case 'azure-data-engineer':    return { badge: 'AZ',  palette: 'secondary' };
    case 'data-analyst-job-ready': return { badge: 'DA',  palette: 'info' };
    default:                       return { badge: 'CT',  palette: 'primary' };
  }
}

function getPdfSrc(file) {
  if (!file) return null;
  const base = file.replace(/\.(pptx?|ppt)$/i, '.pdf');
  return `/slides/${base}`;
}

// ── Compact card rendered in the grid ─────────────────────────────────────
export function SlideCard({ slide, isAdmin, onOpen, onToggleVisibility }) {
  const theme = useTheme();
  const meta = getTrackMeta(slide.track);
  const color = theme.palette[meta.palette]?.main ?? theme.palette.primary.main;
  const cardResource = slide.resources?.find(
    (resource) => resource.cardCta && (!resource.locked || slide.unlockedResources?.[resource.href])
  );

  return (
    <Paper
      variant="outlined"
      onClick={onOpen}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        borderRadius: 2,
        overflow: 'hidden',
        borderColor: slide.visible
          ? alpha(color, 0.45)
          : alpha(theme.palette.divider, 0.6),
        transition: 'box-shadow 0.18s, border-color 0.18s',
        '&:hover': {
          boxShadow: theme.shadows[4],
          borderColor: alpha(color, 0.75),
        },
      }}
    >
      {/* Track-colour top accent */}
      <Box sx={{ height: 4, bgcolor: color, flexShrink: 0 }} />

      <Box sx={{ px: 2, pt: 1.5, pb: 1.25, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Badges row */}
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
          <Chip
            label={meta.badge}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.6rem',
              fontWeight: 800,
              letterSpacing: 0.6,
              bgcolor: alpha(color, 0.12),
              color,
              border: `1px solid ${alpha(color, 0.3)}`,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
          {isAdmin && (
            <Chip
              label={slide.visible ? 'Live' : 'Hidden'}
              size="small"
              color={slide.visible ? 'success' : 'default'}
              sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.75 } }}
            />
          )}
        </Stack>

        {/* Title */}
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{
            lineHeight: 1.35,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            mb: 1,
            flex: 1,
          }}
        >
          {slide.title}
        </Typography>

        {/* Topic chips — first 2 only */}
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {(slide.topics || []).slice(0, 2).map((t) => (
            <Chip
              key={t}
              label={t}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.6rem', height: 16, maxWidth: 140, '& .MuiChip-label': { px: 0.6 } }}
            />
          ))}
          {slide.topics?.length > 2 && (
            <Typography variant="caption" color="text.disabled" sx={{ alignSelf: 'center', fontSize: '0.6rem' }}>
              +{slide.topics.length - 2}
            </Typography>
          )}
        </Stack>

        {cardResource && (
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              onOpen?.();
            }}
            sx={{
              mt: 1,
              alignSelf: 'flex-start',
              minHeight: 24,
              px: 1,
              py: 0.2,
              fontSize: '0.68rem',
              fontWeight: 700,
              textTransform: 'none',
              borderColor: alpha(color, 0.35),
              color,
              '&:hover': {
                borderColor: alpha(color, 0.75),
                bgcolor: alpha(color, 0.06),
              },
            }}
          >
            {cardResource.ctaLabel || cardResource.label}
          </Button>
        )}
      </Box>

      {/* Admin quick-toggle (stops card-click propagation) */}
      {isAdmin && (
        <Box
          sx={{ px: 2, py: 0.75, borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}` }}
          onClick={(e) => e.stopPropagation()}
        >
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(slide.visible)}
                onChange={(e) => onToggleVisibility?.(slide.id, e.target.checked)}
                size="small"
                color="success"
              />
            }
            label={
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.67rem' }}>
                {slide.visible ? 'Visible to learners' : 'Hidden'}
              </Typography>
            }
            sx={{ mr: 0 }}
          />
        </Box>
      )}
    </Paper>
  );
}

// ── Full viewer — rendered inside the Dialog ───────────────────────────────
export default function SlideViewer({ slide, isAdmin, onToggleVisibility, onScopeChange, onResourceUnlockChange, onClose }) {
  const theme = useTheme();
  const [loaded, setLoaded] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [pdfReady, setPdfReady] = useState(false);
  const [resourceModal, setResourceModal] = useState(null);
  const [resourceMarkdown, setResourceMarkdown] = useState('');
  const [resourceLoading, setResourceLoading] = useState(false);
  const [resourceError, setResourceError] = useState('');

  const meta  = getTrackMeta(slide?.track);
  const color = theme.palette[meta.palette]?.main ?? theme.palette.primary.main;
  const pdfPath = slide?.kind === 'note' ? null : getPdfSrc(slide?.file);

  useEffect(() => {
    if (!pdfPath) return;
    let objectUrl = null;
    setBlobUrl(null);
    setPdfReady(false);
    setLoaded(false);

    fetch(pdfPath, { cache: 'no-cache' })
      .then((r) => {
        if (!r.ok) throw new Error('not found');
        return r.blob();
      })
      .then((blob) => {
        if (!blob.type.includes('pdf')) throw new Error('not a pdf');
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
        setPdfReady(true);
      })
      .catch(() => setPdfReady(false));

    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [pdfPath]);

  useEffect(() => {
    if (resourceModal?.modal !== 'markdown') {
      setResourceMarkdown('');
      setResourceLoading(false);
      setResourceError('');
      return;
    }

    const [resourcePath] = resourceModal.href.split('#');
    let cancelled = false;
    setResourceMarkdown('');
    setResourceError('');
    setResourceLoading(true);

    fetch(resourcePath, { cache: 'no-cache' })
      .then((response) => {
        if (!response.ok) throw new Error('not found');
        return response.text();
      })
      .then((markdown) => {
        if (!cancelled) setResourceMarkdown(markdown);
      })
      .catch(() => {
        if (!cancelled) setResourceError('Could not load this resource.');
      })
      .finally(() => {
        if (!cancelled) setResourceLoading(false);
      });

    return () => { cancelled = true; };
  }, [resourceModal]);

  if (!slide) return null;

  const scopeLabel = TRACK_SCOPE_OPTIONS.find((o) => o.value === (slide.scope || 'all'))?.label ?? 'All cohorts';
  const unlockedResources = slide.unlockedResources || {};
  const visibleResources = (slide.resources || []).filter(
    (resource) => isAdmin || !resource.locked || unlockedResources[resource.href]
  );
  const resourcePath = resourceModal?.href?.split('#')[0] || '';
  const isDuplicatePracticeResource = resourcePath === DUPLICATE_PRACTICE_RESOURCE;
  const isMissingnessPracticeResource = resourcePath === MISSINGNESS_PRACTICE_RESOURCE;
  const isOutlierPracticeResource = resourcePath === OUTLIER_PRACTICE_RESOURCE;
  const isDqMath05PracticeResource = resourcePath === DQ_MATH_05_PRACTICE_RESOURCE;
  // Every gradable practice file (Statistics Clinic, the Excel scenario
  // clinic, CT 08's Table Detective) shares the same "Mixed DIY Practice
  // Cases" split marker and TakeHomePracticeBank mount pattern — one lookup
  // instead of a growing isXxxPracticeResource flag per topic.
  const statsClinicConfig = STATS_CLINIC_CONFIG_BY_RESOURCE[resourcePath] || null;
  const collapsedResourceMarkdown = collapseDetailsByDefault(resourceMarkdown);
  const duplicateMarkdownParts = isDuplicatePracticeResource
    ? collapsedResourceMarkdown.split('## Learner Practice: 8 Blank Tables')
    : null;
  const duplicateIntroMarkdown = duplicateMarkdownParts?.[0] || collapsedResourceMarkdown;
  const duplicateTailMarkdown = duplicateMarkdownParts?.[1] || '';
  const duplicateChecklistMarkdown = duplicateTailMarkdown.includes('## Quick Marking Checklist')
    ? `## Quick Marking Checklist${duplicateTailMarkdown.split('## Quick Marking Checklist').slice(1).join('## Quick Marking Checklist')}`
    : '';
  // Statistics Clinic practice files hold Solved Examples followed by an
  // inert "Mixed DIY Practice Cases" answer-field template — the part
  // TakeHomePracticeBank replaces with a gradable form. Keep only the
  // Solved Examples intro; there's no separate Home Tests section any more
  // (the DIY practice cases are the home task).
  const statsMarkdownParts = statsClinicConfig
    ? collapsedResourceMarkdown.split(/# Mixed DIY Practice Cases/)
    : null;
  const statsIntroMarkdown = statsMarkdownParts?.[0] || collapsedResourceMarkdown;
  const formattedDate = slide.date
    ? new Date(slide.date).toLocaleDateString('en-GB', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      })
    : null;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: alpha(color, 0.04) }}>
        <Stack direction="row" alignItems="flex-start">
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
              <Chip
                label={meta.badge}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 800,
                  letterSpacing: 0.5,
                  bgcolor: alpha(color, 0.12),
                  color,
                  border: `1px solid ${alpha(color, 0.3)}`,
                  '& .MuiChip-label': { px: 0.9 },
                }}
              />
              {isAdmin && (
                <Chip
                  label={slide.visible ? 'Live' : 'Hidden'}
                  size="small"
                  color={slide.visible ? 'success' : 'default'}
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              )}
            </Stack>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.25 }}>
              {slide.title}
            </Typography>
            {formattedDate && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                <CalendarIcon sx={{ fontSize: '0.75rem', color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">{formattedDate}</Typography>
              </Stack>
            )}
          </Box>
          {onClose && (
            <IconButton size="small" onClick={onClose} sx={{ ml: 1, mt: -0.25 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        {/* Admin controls */}
        {isAdmin && (
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 1.75 }}>
            <Tooltip title="Choose which cohort sees this slide">
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <PublicIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                <Select
                  value={slide.scope || 'all'}
                  onChange={(e) => onScopeChange?.(slide.id, e.target.value)}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem', '& .MuiSelect-select': { py: 0.4, px: 1 }, minWidth: 160 }}
                >
                  {TRACK_SCOPE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.8rem' }}>{o.label}</MenuItem>
                  ))}
                </Select>
              </Stack>
            </Tooltip>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(slide.visible)}
                  onChange={(e) => onToggleVisibility?.(slide.id, e.target.checked)}
                  size="small"
                  color="success"
                />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  {slide.visible ? 'Visible to learners' : 'Hidden from learners'}
                </Typography>
              }
              sx={{ mr: 0 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto !important' }}>
              Showing to: <strong>{scopeLabel}</strong>
            </Typography>
          </Stack>
        )}
      </Box>

      {/* Topic chips */}
      {slide.topics?.length > 0 && (
        <Stack direction="row" spacing={0.75} sx={{ px: 3, pt: 2, flexWrap: 'wrap', rowGap: 0.75 }}>
          {slide.topics.map((t) => (
            <Chip key={t} label={t} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
          ))}
        </Stack>
      )}

      {visibleResources.length > 0 && (
        <Box sx={{ px: 3, pt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            Resources
          </Typography>
          <Stack spacing={0.75} sx={{ mt: 0.75 }}>
            {visibleResources.map((resource) => {
              const isUnlocked = Boolean(unlockedResources[resource.href]);
              return (
                <Stack key={resource.href} direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                  {resource.modal ? (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setResourceModal(resource)}
                      sx={{ p: 0, minWidth: 0, fontSize: '0.82rem', fontWeight: 600, textTransform: 'none' }}
                    >
                      {resource.label}
                    </Button>
                  ) : (
                    <Link
                      href={resource.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.82rem', fontWeight: 600 }}
                    >
                      {resource.label}
                      <OpenInNewIcon sx={{ fontSize: '0.9rem' }} />
                    </Link>
                  )}
                  {resource.locked && (
                    <Chip
                      icon={isUnlocked ? <LockOpenIcon /> : <LockIcon />}
                      label={isUnlocked ? 'Unlocked for learners' : 'Locked'}
                      size="small"
                      color={isUnlocked ? 'success' : 'default'}
                      variant={isUnlocked ? 'filled' : 'outlined'}
                      onClick={
                        isAdmin
                          ? () => onResourceUnlockChange?.(slide.id, resource.href, !isUnlocked)
                          : undefined
                      }
                      sx={{
                        height: 20,
                        fontSize: '0.62rem',
                        cursor: isAdmin ? 'pointer' : 'default',
                        '& .MuiChip-icon': { fontSize: '0.78rem' },
                      }}
                    />
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Box>
      )}

      <Dialog
        open={Boolean(resourceModal)}
        onClose={() => setResourceModal(null)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {resourceModal?.label}
          <IconButton
            aria-label="Close"
            onClick={() => setResourceModal(null)}
            sx={{ position: 'absolute', right: 12, top: 10 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'background.default' }}>
          {resourceModal?.modal === 'image' && (
            <Box
              component="img"
              src={resourceModal.href}
              alt={resourceModal.label}
              sx={{
                display: 'block',
                width: '100%',
                maxHeight: '78vh',
                objectFit: 'contain',
                bgcolor: 'white',
                borderRadius: 1,
              }}
            />
          )}
          {resourceModal?.modal === 'markdown' && (
            <Box
              sx={{
                maxHeight: '75vh',
                overflow: 'auto',
                bgcolor: 'background.paper',
                borderRadius: 1,
                px: { xs: 2, sm: 4 },
                py: 3,
                color: 'text.primary',
                '& h1': { fontSize: '1.7rem', lineHeight: 1.2, mt: 0, mb: 1.5 },
                '& h2': { fontSize: '1.2rem', mt: 3, mb: 1 },
                '& h3': { fontSize: '1rem', mt: 2.25, mb: 0.75 },
                '& p, & li': { fontSize: '0.92rem', lineHeight: 1.7 },
                '& ul, & ol': { pl: 3 },
                '& blockquote': {
                  m: 0,
                  my: 2,
                  px: 2,
                  py: 1,
                  borderLeft: `4px solid ${color}`,
                  bgcolor: alpha(color, 0.06),
                  color: 'text.secondary',
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  my: 2,
                  fontSize: '0.84rem',
                },
                '& th': {
                  textAlign: 'left',
                  bgcolor: alpha(color, 0.09),
                  fontWeight: 800,
                },
                '& th, & td': {
                  border: `1px solid ${theme.palette.divider}`,
                  px: 1.25,
                  py: 0.85,
                  verticalAlign: 'top',
                },
                '& details': {
                  my: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  bgcolor: alpha(color, 0.035),
                  overflow: 'hidden',
                },
                '& summary': {
                  cursor: 'pointer',
                  px: 1.5,
                  py: 1,
                  fontWeight: 800,
                  bgcolor: alpha(color, 0.08),
                },
                '& details > table, & details > p, & details > ul, & details > ol': {
                  mx: 1.5,
                },
                '& code': {
                  px: 0.5,
                  py: 0.2,
                  borderRadius: 0.5,
                  bgcolor: alpha(theme.palette.text.primary, 0.07),
                  fontSize: '0.85em',
                },
              }}
            >
              {resourceLoading && <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1 }} />}
              {resourceError && (
                <Typography color="error" variant="body2">{resourceError}</Typography>
              )}
              {!resourceLoading && !resourceError && (
                isDuplicatePracticeResource ? (
                  <>
                    <Box dangerouslySetInnerHTML={{ __html: marked(duplicateIntroMarkdown) }} />
                    <DuplicatePracticeBank />
                    {duplicateChecklistMarkdown && (
                      <Box sx={{ mt: 3 }} dangerouslySetInnerHTML={{ __html: marked(duplicateChecklistMarkdown) }} />
                    )}
                  </>
                ) : isMissingnessPracticeResource ? (
                  <>
                    <Box dangerouslySetInnerHTML={{ __html: marked(collapsedResourceMarkdown) }} />
                    <MissingnessPracticeBank />
                  </>
                ) : isOutlierPracticeResource ? (
                  <>
                    <Box dangerouslySetInnerHTML={{ __html: marked(collapsedResourceMarkdown) }} />
                    <OutlierPracticeBank />
                  </>
                ) : isDqMath05PracticeResource ? (
                  <>
                    <Box dangerouslySetInnerHTML={{ __html: marked(collapsedResourceMarkdown) }} />
                    <TakeHomePracticeBank config={DQ_MATH_05_CONFIG} />
                  </>
                ) : statsClinicConfig ? (
                  <>
                    <Box dangerouslySetInnerHTML={{ __html: marked(statsIntroMarkdown) }} />
                    <TakeHomePracticeBank config={statsClinicConfig} />
                  </>
                ) : (
                  <Box dangerouslySetInnerHTML={{ __html: marked(collapsedResourceMarkdown) }} />
                )
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF embed */}
      <Box sx={{ px: 3, pt: 2, pb: 3 }}>
        {pdfReady ? (
          <Box
            sx={{
              position: 'relative', width: '100%', paddingTop: '62%',
              borderRadius: 1.5, overflow: 'hidden',
              bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5',
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            {!loaded && (
              <Skeleton variant="rectangular" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
            )}
            <embed
              key={blobUrl}
              src={blobUrl}
              type="application/pdf"
              onLoad={() => setLoaded(true)}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
            />
          </Box>
        ) : (
          <Box sx={{ border: `1px dashed ${theme.palette.divider}`, borderRadius: 1.5, p: 4, textAlign: 'center' }}>
            <SlideshowIcon sx={{ fontSize: '2.5rem', color: 'text.disabled', mb: 1 }} />
            {slide.kind === 'note' ? (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  This clinic is a deep lesson note, not a slide deck.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Open the lesson note from the resources above.
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  PDF not yet generated for this slide.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  In Windows PowerShell:{' '}
                  <code>powershell -ExecutionPolicy Bypass -File scripts\convert_slides.ps1</code>
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
